// src/contexts/AuthContext.tsx
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useActionState } from 'react';
import { User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { TalentData } from '../types/talent';
import { PromoterData } from '../types/promoter';
import { signOut } from 'firebase/auth';
import { NavigateFunction, useNavigate } from 'react-router';

interface UserStatus {
  isAuthenticated: boolean;
  isLoading: boolean;
  hasProfile: boolean;
  userType: 'talent' | 'promoter' | null;
  profile: TalentData | PromoterData | null;  // Change this
  error: string | null;
  isInitialized: boolean;
}

type LogoutAction = (prev: string | null, navigate: NavigateFunction) => Promise<string | null>;


interface AuthContextType extends UserStatus {
  user: User | null;
  refreshProfile: () => Promise<void>;
  clearProfile: () => void;
  logout: (navigate: NavigateFunction) => Promise<void>;
  logoutError: string | null;
  isLoggingOut: boolean;
}


export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const MAX_AUTH_RETRIES = 3;
const AUTH_RETRY_DELAY = 1000; // 1 second

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<UserStatus>({
    isAuthenticated: false,
    isLoading: true,
    hasProfile: false,
    userType: null,
    profile: null,
    error: null,
    isInitialized: false,
  });
  const [authRetries, setAuthRetries] = useState(0);

  const navigate = useNavigate();

  const [logoutError, submitLogout, isLoggingOut] = useActionState<string | null, NavigateFunction>(
    // Properly type the previousState parameter even though we don't use it
    async (_previousState: string | null, navigate: NavigateFunction) => {
      try {
        await signOut(auth);
        clearProfile();
        navigate('/', { replace: true });
        return null;
      } catch (error) {
        console.error('Logout error:', error);
        return 'Logout failed';
      }
    },
    null
  );

  const refreshProfile = async () => {
    if (!user) {
      setStatus(prev => ({
        ...prev,
        isAuthenticated: false,
        hasProfile: false,
        profile: null,
        userType: null,
        isLoading: false
      }));
      return;
    }
  
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        setStatus(prev => ({
          ...prev,
          isAuthenticated: true,
          hasProfile: true,
          userType: data.type,
          profile: data.type === 'promoter' ? data.promoterData : data.talentData || null,
          isLoading: false
        }));
      } else {
        setStatus(prev => ({
          ...prev,
          isAuthenticated: true,
          hasProfile: false,
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('Profile refresh error:', error);
      setStatus(prev => ({
        ...prev,
        error: 'Failed to load profile',
        isLoading: false
      }));
    }
  };

  const clearProfile = () => {
    setStatus(prev => ({
      ...prev,
      hasProfile: false,
      userType: null,
      profile: null
    }));
  };

  // Handle initial auth state
  useEffect(() => {
    let retryTimeout: NodeJS.Timeout;

    const handleAuthStateChange = async (firebaseUser: User | null) => {
      console.debug('[Auth Debug] Auth state changed:', {
        userId: firebaseUser?.uid,
        timestamp: new Date().toISOString(),
        platform: window.innerWidth < 768 ? 'mobile' : 'desktop',
        pathname: window.location.pathname,
        retryCount: authRetries
      });

      if (!firebaseUser) {
        setUser(null);
        setStatus(prev => ({
          ...prev,
          isAuthenticated: false,
          isLoading: false,
          hasProfile: false,
          userType: null,
          profile: null,
          isInitialized: true
        }));
        return;
      }

      setUser(firebaseUser);

      try {
        console.debug('[Auth Debug] Fetching user doc for:', firebaseUser.uid);
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        
        // Reset retries on successful fetch
        setAuthRetries(0);
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          const redirectPath = localStorage.getItem('redirectAfterAuth');
          const currentPath = window.location.pathname;
          
          console.debug('[Auth Debug] Navigation state:', {
            hasProfile: true,
            userType: data.type,
            redirectPath,
            currentPath,
            userId: firebaseUser.uid,
            isRootOrAuth: currentPath === '/' || currentPath === '/auth'
          });

          setStatus({
            isAuthenticated: true,
            isLoading: false,
            hasProfile: true,
            userType: data.type,
            profile: data.type === 'promoter' ? data.promoterData : data.talentData,
            error: null,
            isInitialized: true,
          });

          // Handle navigation after auth
          if (redirectPath) {
            console.debug('[Auth Debug] Navigating to redirect path:', redirectPath);
            localStorage.removeItem('redirectAfterAuth');
            navigate(redirectPath, { replace: true });
          } else if ((currentPath === '/' || currentPath === '/auth') && firebaseUser?.uid) {
            // Ensure we have all required data before constructing the path
            if (data.type && firebaseUser.uid) {
              const profilePath = `/${data.type}/profile/${firebaseUser.uid}`;
              
              console.debug('[Auth Debug] Constructing profile path:', {
                userType: data.type,
                userId: firebaseUser.uid,
                resultPath: profilePath
              });

              navigate(profilePath, { replace: true });
            } else {
              console.error('[Auth Debug] Missing required data for navigation:', {
                userType: data.type,
                userId: firebaseUser.uid
              });
            }
          }
        } else {
          console.debug('[Auth Debug] No user doc found');
          setStatus(prev => ({
            ...prev,
            isAuthenticated: true,
            isLoading: false,
            hasProfile: false,
            userType: null,
            profile: null,
            error: null,
            isInitialized: true,
          }));
        }
      } catch(error) {
        console.error('[Auth Debug] Error in auth flow:', {
          error,
          userId: firebaseUser?.uid,
          pathname: window.location.pathname,
          retryCount: authRetries
        });

        // Retry logic for Safari CORS issues
        if (authRetries < MAX_AUTH_RETRIES) {
          setAuthRetries(prev => prev + 1);
          retryTimeout = setTimeout(() => {
            handleAuthStateChange(firebaseUser);
          }, AUTH_RETRY_DELAY);
        } else {
          setStatus({
            isAuthenticated: true,
            isLoading: false,
            hasProfile: false,
            userType: null,
            profile: null,
            error: 'Failed to load profile',
            isInitialized: true,
          });
        }
      }
    };

    const unsubscribe = onAuthStateChanged(auth, handleAuthStateChange);

    return () => {
      unsubscribe();
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [navigate, authRetries]);


  const logout = async (navigate: NavigateFunction) => {
    await submitLogout(navigate);
  };

  return (
    <AuthContext
      value={{
        user,
        refreshProfile,
        clearProfile,
        logout,
        logoutError,
        isLoggingOut,
        ...status
      }}
    >
      {children}
    </AuthContext>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};